/**
 * Form Validation Utilities for Nasi iSpani
 * Includes email, phone number, and general form validation
 */

/**
 * Validates email address format
 * @param {string} email - Email address to validate
 * @returns {object} - Validation result with isValid and error message
 */
export const validateEmail = (email) => {
  const result = {
    isValid: false,
    error: null
  };

  if (!email || email.trim() === '') {
    result.error = 'Email address is required';
    return result;
  }

  // Comprehensive email regex pattern
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!emailRegex.test(email.trim())) {
    result.error = 'Please enter a valid email address';
    return result;
  }

  // Additional checks for common issues
  const trimmedEmail = email.trim().toLowerCase();

  // Check for consecutive dots
  if (trimmedEmail.includes('..')) {
    result.error = 'Email address cannot contain consecutive dots';
    return result;
  }

  // Check for valid domain
  const parts = trimmedEmail.split('@');
  if (parts.length !== 2) {
    result.error = 'Email address must contain exactly one @ symbol';
    return result;
  }

  const [localPart, domain] = parts;

  // Check local part length
  if (localPart.length === 0 || localPart.length > 64) {
    result.error = 'Email address local part must be between 1 and 64 characters';
    return result;
  }

  // Check domain length
  if (domain.length === 0 || domain.length > 253) {
    result.error = 'Email domain must be between 1 and 253 characters';
    return result;
  }

  // Check for valid domain format
  if (domain.startsWith('.') || domain.endsWith('.') || domain.startsWith('-') || domain.endsWith('-')) {
    result.error = 'Invalid email domain format';
    return result;
  }

  result.isValid = true;
  return result;
};

/**
 * Validates South African phone number
 * Supports formats: 0123456789, +27123456789, 27123456789
 * @param {string} phoneNumber - Phone number to validate
 * @returns {object} - Validation result with isValid, error, and formatted number
 */
export const validatePhoneNumber = (phoneNumber) => {
  const result = {
    isValid: false,
    error: null,
    formatted: null
  };

  if (!phoneNumber || phoneNumber.trim() === '') {
    result.error = 'Phone number is required';
    return result;
  }

  // Remove all spaces, dashes, and parentheses
  const cleanNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');

  // South African phone number patterns
  const patterns = [
    /^0[1-9][0-9]{8}$/,           // 0123456789 (10 digits starting with 0)
    /^\+27[1-9][0-9]{8}$/,        // +27123456789 (12 chars with +27)
    /^27[1-9][0-9]{8}$/           // 27123456789 (11 digits with 27)
  ];

  let isValidFormat = false;
  let formattedNumber = '';

  // Check each pattern
  for (const pattern of patterns) {
    if (pattern.test(cleanNumber)) {
      isValidFormat = true;
      
      // Format to standard +27 format
      if (cleanNumber.startsWith('0')) {
        // Convert 0123456789 to +27123456789
        formattedNumber = '+27' + cleanNumber.substring(1);
      } else if (cleanNumber.startsWith('+27')) {
        // Already in correct format
        formattedNumber = cleanNumber;
      } else if (cleanNumber.startsWith('27')) {
        // Convert 27123456789 to +27123456789
        formattedNumber = '+' + cleanNumber;
      }
      break;
    }
  }

  if (!isValidFormat) {
    result.error = 'Please enter a valid South African phone number (10 digits starting with 0, or with +27 country code)';
    return result;
  }

  // Additional validation for South African mobile/landline prefixes
  const numberWithoutCountryCode = formattedNumber.replace('+27', '');
  const firstDigit = numberWithoutCountryCode[0];
  const secondDigit = numberWithoutCountryCode[1];

  // Valid South African prefixes
  const validPrefixes = [
    '1', '2', '3', '4', '5', '6', '7', '8', '9' // All digits 1-9 are valid for SA
  ];

  if (!validPrefixes.includes(firstDigit)) {
    result.error = 'Invalid South African phone number prefix';
    return result;
  }

  // Check for common invalid patterns
  if (numberWithoutCountryCode === '0000000000' || 
      numberWithoutCountryCode === '1111111111' ||
      numberWithoutCountryCode === '1234567890') {
    result.error = 'Please enter a valid phone number';
    return result;
  }

  result.isValid = true;
  result.formatted = formattedNumber;
  return result;
};

/**
 * Formats phone number for display
 * @param {string} phoneNumber - Phone number to format
 * @returns {string} - Formatted phone number for display
 */
export const formatPhoneNumberDisplay = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  const validation = validatePhoneNumber(phoneNumber);
  if (validation.isValid) {
    const number = validation.formatted.replace('+27', '');
    // Format as +27 12 345 6789
    return `+27 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
  }
  
  return phoneNumber;
};

/**
 * Validates required fields
 * @param {object} formData - Form data object
 * @param {array} requiredFields - Array of required field names
 * @returns {object} - Validation result with errors object
 */
export const validateRequiredFields = (formData, requiredFields) => {
  const errors = {};
  
  requiredFields.forEach(field => {
    if (!formData[field] || formData[field].toString().trim() === '') {
      errors[field] = `${getFieldDisplayName(field)} is required`;
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Gets display name for form fields
 * @param {string} fieldName - Field name
 * @returns {string} - Display name
 */
export const getFieldDisplayName = (fieldName) => {
  const fieldNames = {
    first_name: 'First Name',
    last_name: 'Last Name',
    email: 'Email Address',
    phone_number: 'Phone Number',
    id_number: 'ID Number',
    date_of_birth: 'Date of Birth',
    gender: 'Gender',
    race: 'Race',
    province: 'Province',
    city: 'City',
    address: 'Street Address',
    postal_code: 'Postal Code',
    bio: 'Bio',
    highest_grade: 'Highest Grade Completed',
    school_name: 'School Name',
    has_matric: 'Matric Status',
    matric_year: 'Matric Year',
    additional_certificates: 'Additional Certificates',
    tertiary_qualification_type: 'Qualification Type',
    tertiary_institution: 'Institution',
    field_of_study: 'Field of Study',
    tertiary_year_completed: 'Year Completed',
    tertiary_qualification_status: 'Qualification Status',
    additional_qualifications: 'Additional Qualifications'
  };
  
  return fieldNames[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Validates complete profile form
 * @param {object} formData - Form data object
 * @returns {object} - Comprehensive validation result
 */
export const validateProfileForm = (formData) => {
  const errors = {};
  
  // Required fields validation
  const requiredFields = ['first_name', 'last_name', 'email'];
  const requiredValidation = validateRequiredFields(formData, requiredFields);
  Object.assign(errors, requiredValidation.errors);
  
  // Email validation
  if (formData.email) {
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      errors.email = emailValidation.error;
    }
  }
  
  // Phone number validation (if provided)
  if (formData.phone_number && formData.phone_number.trim() !== '') {
    const phoneValidation = validatePhoneNumber(formData.phone_number);
    if (!phoneValidation.isValid) {
      errors.phone_number = phoneValidation.error;
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Real-time field validation
 * @param {string} fieldName - Name of the field being validated
 * @param {string} value - Value to validate
 * @returns {object} - Validation result for the specific field
 */
export const validateField = (fieldName, value) => {
  const result = {
    isValid: true,
    error: null,
    formatted: value
  };
  
  switch (fieldName) {
    case 'email':
      const emailValidation = validateEmail(value);
      result.isValid = emailValidation.isValid;
      result.error = emailValidation.error;
      break;
      
    case 'phone_number':
      if (value && value.trim() !== '') {
        const phoneValidation = validatePhoneNumber(value);
        result.isValid = phoneValidation.isValid;
        result.error = phoneValidation.error;
        result.formatted = phoneValidation.formatted || value;
      }
      break;
      
    case 'first_name':
    case 'last_name':
      if (!value || value.trim() === '') {
        result.isValid = false;
        result.error = `${getFieldDisplayName(fieldName)} is required`;
      }
      break;
      
    default:
      // No specific validation for other fields
      break;
  }
  
  return result;
};
